import { Icon, IconName, IconSize } from "@/components/icons/Icon";
import { CheckboxV2Control } from "@/components/interactive/CheckboxV2";
import { IconButtonV2 } from "@/components/interactive/ButtonV2";
import {
  useAlignedSubmenuLayer,
  useSubmenuHoverInteractions,
} from "@/components/interactive/MenuItem.hooks";
import { classNames } from "@/utils/classnames";
import { autoUpdate, flip, offset, shift, useFloating } from "@floating-ui/react";
import { ChalkLink } from "@/components/interactive/ChalkLink";
import { type LinkProps } from "next/link";
import React, { ForwardedRef, forwardRef, useCallback, useState } from "react";

// ------------------------------------------------------------------
// MenuItemV2 — a row inside MenuContainerV2. Five variants cover the
// typical menu-popover content patterns (per Figma):
//
//   default   — clickable row: optional leftIcon, label, optional
//               keyboardShortcut, optional trailing arrow. Supports
//               `active` and `selected` states.
//   title     — non-interactive section heading. Small muted text.
//   search    — search input styled as a menu row (leading magnifier
//               icon + <input>). Figma calls this "Header".
//   checkbox  — toggleable row with a small checkbox on the left.
//   two-line  — row with a primary label and muted subtitle stacked.
//
// All variants have `sm` and `md` sizes. Sizes mirror the paired
// select's chrome so items align when the popover opens under the
// trigger.
//
// Currently presentational — the component emits the right semantics
// (<button role="menuitem"> or a <ChalkLink> for navigational rows),
// but open / close / keyboard-navigation is owned by whatever primitive
// drives the popover.
// ------------------------------------------------------------------

export const MenuItemV2Variants = ["default", "title", "search", "checkbox", "two-line"] as const;
export type MenuItemV2Variant = (typeof MenuItemV2Variants)[number];

export const MenuItemV2Sizes = ["sm", "md"] as const;
export type MenuItemV2Size = (typeof MenuItemV2Sizes)[number];

export const MenuItemV2Intents = ["default", "destructive"] as const;
export type MenuItemV2Intent = (typeof MenuItemV2Intents)[number];

// -----------------------------------------------------------------
// Size / layout tables
// -----------------------------------------------------------------

// Heights per variant × size. `two-line` is intentionally absent — it
// grows to fit its two lines.
const heightClass = {
  sm: {
    default: "h-7", // 28
    checkbox: "h-7",
    search: "h-6", //   24 (per Figma sm popover)
    title: "h-6", //    24
  },
  md: {
    default: "h-8", // 32
    checkbox: "h-8",
    search: "h-7", //  28
    title: "h-7", //   28
  },
} as const;

// Left-side icon (default + search variants).
const leftIconSize: Record<MenuItemV2Size, IconSize> = {
  sm: "sm", // 12px
  md: "md", // 16px
};

// Label text size. Uses the dashboard DS typography scale
// (`text-cds-*`) — resolves to CSS vars defined on the
// `.design-system-typography` ancestor applied by Layout.
const labelTextClass: Record<MenuItemV2Size, string> = {
  sm: "text-cds-sm",
  md: "text-cds-md",
};

// Title variant sits one DS step smaller than the row label at
// each size: sm → 11px (`cds-xs`), md → 12px (`cds-sm`). Uses the
// DS typography scale so line-height + letter-spacing stay
// consistent with the rest of the V2 copy. Values resolve to
// `var(--design-system-text-*-size)` inherited from the
// `.design-system-typography` ancestor applied by Layout.
const titleTextClass: Record<MenuItemV2Size, string> = {
  sm: "text-cds-xs",
  md: "text-cds-sm",
};

// Trailing keyboard-shortcut text — always muted, one size smaller
// than the label for visual hierarchy.
const shortcutTextClass: Record<MenuItemV2Size, string> = {
  sm: "text-cds-sm",
  md: "text-cds-sm",
};

// Two-line rows align the icon to the primary label's line box, not
// the full label+subtitle block. Both sm/md labels use a 16px line
// height; sm icons are 12px and md icons are 14px.
const twoLineIconOffsetClass: Record<MenuItemV2Size, string> = {
  sm: "mt-cds-2",
  md: "mt-px",
};

// -----------------------------------------------------------------
// Shared class constants
// -----------------------------------------------------------------
//
// NOTE on `dark-mode.css`: that legacy stylesheet targets
// `.dark [role="menu"]` / `[role="menuitem"]` / `[role="option"]`
// with `!important` rules that would otherwise override our Tailwind
// colors (class+attr specificity beats single-class `!` utilities).
// Instead of fighting each class with more overrides, the V2 menu
// is opted out at the subtree boundary: `MenuContainerV2` sets
// `data-ds-v2` and `dark-mode.css` excludes `[data-ds-v2] *` from
// the legacy selectors. Inside this subtree, plain Tailwind classes
// (no `!` needed) are free to apply.

// Row chrome shared across clickable variants (default / checkbox /
// two-line). Title + search have their own chrome. The checkbox variant
// swaps the left padding per size (see checkboxRowLeftPaddingClass), so the
// left padding stays separable from the rest of the chrome.
//
// The transition deliberately lists properties instead of using
// `transition-colors`, whose set includes `background-color`. The row background
// carries selection and hover state. Those fills need to snap as the value or
// pointer changes so a row that no longer owns a state cannot retain its tint.
// Text and icon color still ease; the fills and outline snap.
const ROW_BASE_WITHOUT_LEFT_PADDING =
  "flex items-center w-full rounded-cds-3 pr-cds-6 py-cds-6 gap-cds-8 text-left " +
  "transition-[color,border-color,text-decoration-color,fill,stroke] duration-75";
const ROW_BASE = `${ROW_BASE_WITHOUT_LEFT_PADDING} pl-cds-8`;

// The checkbox's left inset mirrors its top inset so the control sits
// equidistant from the row's top and left edges: the 16px control leaves
// 6px above in a 28px sm row (and in a subtitle row's py-cds-6 + 16px
// title line box), 8px in a 32px md row.
const checkboxRowLeftPaddingClass: Record<MenuItemV2Size, string> = {
  sm: "pl-cds-6",
  md: "pl-cds-8",
};

// Reserved for a row's leading control so an icon-less row's text starts where an icon-bearing
// sibling's does. Width lands the label exactly where a checkbox row puts it: that row insets by
// `checkboxRowLeftPaddingClass` and then spends 16px on the control, while these rows inset by a
// flat pl-cds-8 -- so the slot absorbs the 2px difference at sm.
//
// Whether to reserve it is a property of the *list*, not of one row: a row cannot see whether a
// sibling has an icon. `reserveLeftSlot` is therefore off by default and set by whatever owns the
// list -- a menu that mixes icon and icon-less rows, or a dropdown's `forceIndent`. A row that
// carries its own leading content always gets the slot regardless.
//
// The width is a minimum, not a fixed size, so an accessory wider than one icon (a chevron beside
// an icon, an avatar) widens the slot instead of bleeding into the label.
const leftSlotWidthClass: Record<MenuItemV2Size, string> = {
  sm: "min-w-3.5", // 14px: 8 + 14 + 8 == 6 + 16 + 8
  md: "min-w-4", //   16px: 8 + 16 + 8 == 8 + 16 + 8
};

// Selection is a persistent fill, and the keyboard cursor shares it by default,
// so a list reads like a native select: one highlighted row that arrow keys move.
// `activeStyle="outline"` opts a widget into an inset blue outline for the cursor
// instead, keeping it readable on top of a selected row. Hover remains the lighter
// transient fill.
const ROW_SELECTED_BG = "bg-zinc-200/60 dark:bg-white/[0.06]";
const ROW_ACTIVE_OUTLINE = "ring-1 ring-inset ring-blue-500 dark:ring-blue-400";
const ROW_HOVER_BG = "hover:bg-zinc-200/30 dark:hover:bg-white/[0.03]";

// Muted color used by title, keyboard shortcut, subtitle, and the
// search row's leading icon. DS convention: light/dark shades rotate
// around zinc-500, so zinc-400 ↔ zinc-600. Light uses `cds-ink-muted`
// (= zinc-400); dark uses direct `zinc-600` — the `dark:` variant
// isn't caught by `dark-mode.css`'s bare `.dark .text-zinc-600`
// remap (that only matches the non-variant class).
//
// `!` prefix matters specifically for SVG icon children: without it,
// `dark-mode.css`'s `.dark svg { color: inherit }` rule beats our
// single-class `.text-cds-ink-muted` on specificity (class+element vs
// class), making the icon inherit the body's zinc-200 and drift away
// from the placeholder color.
const MUTED_TEXT = "!text-cds-ink-muted dark:!text-zinc-500";
const SEARCH_CLEAR_BUTTON_TEXT =
  "[&_svg]:!text-zinc-400 [&:hover_svg]:!text-zinc-500 " +
  "dark:[&_svg]:!text-zinc-500 dark:[&:hover_svg]:!text-zinc-400";

// Same color as MUTED_TEXT, targeted at the ::placeholder pseudo-
// element. Kept in lockstep with MUTED_TEXT so the search row's icon
// and placeholder render the exact same gray. `!` defeats
// `@tailwindcss/forms`' base-layer `input::placeholder { color: … }`.
const MUTED_PLACEHOLDER_TEXT = "placeholder:!text-cds-ink-muted dark:placeholder:!text-zinc-500";

// Default label color (regular menu items, checkbox label, two-line
// title). Active items render with slightly brighter text in dark.
const DEFAULT_LABEL_TEXT = "text-cds-ink dark:text-zinc-300";
const DEFAULT_LABEL_HOVER_TEXT = "hover:!text-zinc-950 dark:hover:!text-white";
const ACTIVE_LABEL_TEXT = "!text-zinc-950 dark:!text-white";
const DEFAULT_LEFT_ICON_TEXT = "menu-item-v2-left-icon !text-cds-ink-subtle dark:!text-zinc-400";

const getRenderedRightIcon = (rightIcon: IconName): IconName => {
  // Dropdown selected-state indicators should be a plain checkmark. The
  // product `check` icon is encircled, so normalize that trailing menu
  // affordance here without changing standalone `Icon name="check"` usage.
  return rightIcon === "check" ? "checkMark" : rightIcon;
};
const DEFAULT_LEFT_ICON_HOVER_TEXT =
  "[&:hover_.menu-item-v2-left-icon]:!text-zinc-800 " +
  "dark:[&:hover_.menu-item-v2-left-icon]:!text-white";
const ACTIVE_LEFT_ICON_TEXT =
  "[&_.menu-item-v2-left-icon]:!text-zinc-800 " + "dark:[&_.menu-item-v2-left-icon]:!text-white";
const INTERACTIVE_CURSOR = "cursor-pointer disabled:cursor-not-allowed";
const DESTRUCTIVE_LABEL_TEXT = "text-red-600 dark:text-red-400";
const DESTRUCTIVE_HOVER_TEXT = "hover:!text-red-600 dark:hover:!text-red-400";
const DESTRUCTIVE_HOVER_BG = "hover:bg-red-50 dark:hover:bg-red-500/10";
const DESTRUCTIVE_LEFT_ICON_TEXT = "menu-item-v2-left-icon !text-red-600 dark:!text-red-400";
const DESTRUCTIVE_LEFT_ICON_HOVER_TEXT =
  "[&:hover_.menu-item-v2-left-icon]:!text-red-600 " +
  "dark:[&:hover_.menu-item-v2-left-icon]:!text-red-400";
const ACTIVE_DESTRUCTIVE_TEXT =
  "!text-red-600 dark:!text-red-400 [&_.menu-item-v2-left-icon]:!text-red-600 " +
  "dark:[&_.menu-item-v2-left-icon]:!text-red-400";

// -----------------------------------------------------------------
// Props
// -----------------------------------------------------------------

export interface MenuItemV2Props extends Omit<React.ComponentProps<"button">, "ref" | "children"> {
  variant?: MenuItemV2Variant;
  size?: MenuItemV2Size;
  /** Main row text. Required for every variant. */
  label: React.ReactNode;
  /**
   * Replaces the label region of the `default`, `checkbox`, and `two-line`
   * variants with caller-owned content, keeping the row's chrome. `label` is
   * still required and stays the row's accessible name, so pass a plain string
   * there even when `content` renders something richer.
   *
   * It replaces the *label line* only, so on a fixed-height variant it gets one
   * line's worth of room. Content that needs a second line belongs in
   * `subtitle`, whose variants grow.
   */
  labelContent?: React.ReactNode;
  /** Optional Next.js href for rows that should navigate as links. */
  href?: LinkProps["href"];
  /** Use Next.js shallow routing for navigational rows. */
  shallow?: LinkProps["shallow"];
  /** Open navigational rows in a new tab. */
  shouldOpenInNewTab?: boolean;
  /** Hide the trailing new-tab indicator when this item already conveys that action. */
  hideOpenInNewTabIcon?: boolean;
  /** Leading icon (default + search variants). */
  leftIcon?: IconName;
  /** Custom leading content (default, checkbox, and two-line variants). */
  leftAccessory?: React.ReactNode;
  /**
   * Keep the leading-control slot even when this row has no `leftIcon` or
   * `leftAccessory`, so its text starts on the same indent as an icon-bearing
   * sibling.
   *
   * Off by default: a list where nothing has a leading control wants its labels
   * against the row padding, not behind an empty 14px. Set it from whatever owns
   * the list — a hand-built menu that mixes icon and icon-less rows, or a
   * dropdown's `forceIndent`, which passes it to every option at once. A row
   * carrying its own leading content keeps the slot either way.
   */
  reserveLeftSlot?: boolean;
  /**
   * Align custom accessories to the primary label's 16px line box. Dropdown
   * options use this so accessory content stays centered on the label. In a
   * two-line dropdown row, the accessories remain on the primary line and the
   * subtitle takes a second line that starts on the label's edge, past the
   * leading slot, without changing ordinary menus.
   */
  alignAccessoriesToLabelLine?: boolean;
  /** Trailing keyboard shortcut (default variant only). */
  keyboardShortcut?: string;
  /** Trailing icon (default variant only). */
  rightIcon?: IconName;
  /** Optional className for the trailing icon (default variant only). */
  rightIconClassName?: string;
  /** Trailing custom content (default, checkbox, and two-line variants). */
  rightAccessory?: React.ReactNode;
  /** When true, renders a persistent highlighted/current row surface without a checkmark. */
  active?: boolean;
  /** Render active as the usual fill, or as a keyboard-cursor outline. */
  activeStyle?: "fill" | "outline";
  /** When true, renders the selected-option surface and a trailing checkmark. */
  selected?: boolean;
  /** Preserve the selected surface and semantics without rendering a checkmark. */
  hideSelectedIndicator?: boolean;
  /** Transient row highlight for keyboard/mouse focus inside menu controllers.
   *  Unlike `active`, this does not imply the row is selected/current. */
  highlighted?: boolean;
  /** Destructive rows use red text/icon treatment for actions like delete. */
  intent?: MenuItemV2Intent;
  /** Disable CSS hover visuals when a parent composite owns the active row state. */
  disableHoverStyle?: boolean;
  /** Subtitle content (two-line and checkbox variants). */
  subtitle?: React.ReactNode;
  /**
   * Let a string subtitle wrap onto as many lines as it needs instead of
   * truncating, growing the row with it.
   *
   * Off by default because the usual subtitle is a one-line qualifier in a
   * narrow menu — a path, a count, a timestamp — where a row that silently
   * doubles in height is the surprise. Turn it on for descriptive copy, which a
   * clipped line defeats.
   */
  wrapSubtitle?: boolean;
  /** Checkbox state (checkbox variant only). */
  checked?: boolean;
  /** Search input value (search variant only). */
  searchValue?: string;
  /** Search onChange (search variant only). */
  onSearchChange?: (value: string) => void;
  /** Input type for text-entry menu rows. Numeric filter chips use `number`. */
  searchInputType?: "number" | "search";
  /** Keep a text-entry-only search at title-row height. Searches that filter
   *  menu options match the height of a normal menu item by default. */
  compactSearch?: boolean;
  /** Search onKeyDown (search variant only). */
  onSearchKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  /** Ref for the underlying search input (search variant only). */
  searchInputRef?: React.Ref<HTMLInputElement>;
  /** aria-activedescendant for input-driven keyboard navigation. */
  searchAriaActivedescendant?: string;
  /**
   * Id of the `role="listbox"` element this search row drives, which makes the
   * input that listbox's combobox.
   *
   * Pass it whenever `searchAriaActivedescendant` is set and the rows below are
   * options: an active descendant only resolves to something the input owns, and
   * a search row's rows are its siblings in the menu, not its children.
   * `aria-controls` on a combobox is what supplies that ownership. Without it the
   * input stays a plain searchbox naming a descendant it does not have.
   */
  searchListboxId?: string;
  /** Draw a divider along the bottom edge of title/search header rows. */
  showBottomDivider?: boolean;
  /** Omit the leading magnifier icon on the search variant. Used
   *  for menus whose items are also icon-less, so the search row
   *  reads as a plain input row. */
  hideSearchIcon?: boolean;
  /** Submenu content (default and two-line variants only). When provided, a
   *  trailing chevron is rendered and hovering the row opens
   *  `submenu` in a popover to the right. Typically this is another
   *  `<MenuContainerV2>` tree — but any node works.
   *  Presentational only: no keyboard navigation, no focus trap. */
  submenu?: React.ReactNode;
}

const isModifiedNavigationClick = (event: React.MouseEvent) =>
  event.metaKey || event.ctrlKey || event.altKey || event.shiftKey || event.button !== 0;

/** Matches open flyout submenu layers; enclosing menus count these before closing on mouse-leave. */
export const MENU_SUBMENU_LAYER_SELECTOR = "[data-menu-submenu-layer]";

type MenuItemV2InteractiveElement = HTMLButtonElement | HTMLAnchorElement;

// -----------------------------------------------------------------
// Dispatch by variant — each branch returns its own JSX tree. Keeps
// each variant's layout + class list readable at a glance; no attempt
// to squeeze all five into one conditional mess.
// -----------------------------------------------------------------

const MenuItemV2Base = forwardRef<MenuItemV2InteractiveElement, MenuItemV2Props>(
  function MenuItemV2Impl(props: MenuItemV2Props, ref) {
    switch (props.variant ?? "default") {
      case "title":
        return <TitleRow {...props} />;
      case "search":
        return <SearchRow {...props} />;
      case "checkbox":
        return <CheckboxRow {...props} ref={ref as ForwardedRef<HTMLButtonElement>} />;
      case "two-line":
        return <TwoLineRow {...props} ref={ref} />;
      case "default":
      default:
        return <DefaultRow {...props} ref={ref} />;
    }
  },
);

export const MenuItemV2 = React.memo(MenuItemV2Base);
MenuItemV2.displayName = "MenuItemV2";

// -----------------------------------------------------------------
// Default row — icon / label / shortcut / optional right icon /
// optional submenu arrow.
//
// Two modes, driven by the `submenu` prop:
//   - Without submenu: pure button row, no trailing chevron.
//   - With submenu: trailing chevron-right; hovering the row (or
//     the submenu itself) opens the submenu in a Floating UI popover
//     to the right, flipping left when it would overflow the viewport.
//     `safePolygon` (via useSubmenuHoverInteractions) keeps the popover
//     open while the cursor travels the diagonal toward it.
// -----------------------------------------------------------------

const DefaultRow = forwardRef(function DefaultRowImpl(
  {
    size = "md",
    label,
    labelContent,
    leftIcon,
    leftAccessory,
    reserveLeftSlot = false,
    alignAccessoriesToLabelLine = false,
    keyboardShortcut,
    rightIcon,
    rightIconClassName,
    rightAccessory,
    active = false,
    activeStyle = "fill",
    selected = false,
    hideSelectedIndicator = false,
    highlighted = false,
    intent = "default",
    disableHoverStyle = false,
    disabled,
    href,
    shallow,
    shouldOpenInNewTab,
    hideOpenInNewTabIcon = false,
    submenu,
    className,
    role,
    type = "button",
    onClick,
    onMouseEnter,
    ...rest
  }: MenuItemV2Props,
  ref: ForwardedRef<MenuItemV2InteractiveElement>,
) {
  const hasSubmenu = submenu != null;
  const [submenuOpen, setSubmenuOpen] = useState(false);
  const { submenuTopOffset, updateSubmenuTop } = useAlignedSubmenuLayer(submenuOpen);
  const { refs, floatingStyles, context } = useFloating({
    open: submenuOpen,
    onOpenChange: setSubmenuOpen,
    placement: "right-start",
    strategy: "absolute",
    middleware: [
      offset({
        // Preserve the existing 3px gutter. The measured cross-axis
        // offset keeps the child menu's first selectable row aligned
        // with the parent row, even when the child menu has a title.
        mainAxis: 3,
        crossAxis: submenuTopOffset ?? -3,
      }),
      flip({ padding: 8 }),
      shift({ padding: 8 }),
    ],
    whileElementsMounted: autoUpdate,
  });
  const { getReferenceProps, getFloatingProps } = useSubmenuHoverInteractions(context, hasSubmenu);
  const setFloatingSubmenuRef = useCallback(
    (node: HTMLDivElement | null) => {
      refs.setFloating(node);
      updateSubmenuTop(node);
    },
    [refs, updateSubmenuTop],
  );

  // useHover covers real pointer traffic on the wrapper; this direct
  // path additionally opens on row focus and on synthetic mouseenter
  // dispatched straight at the row (keyboard flows and tests).
  const openSubmenu = useCallback(() => {
    if (!hasSubmenu) return;
    setSubmenuOpen(true);
  }, [hasSubmenu]);

  const handleRowMouseEnter = useCallback<React.MouseEventHandler<HTMLButtonElement>>(
    (event) => {
      openSubmenu();
      onMouseEnter?.(event);
    },
    [openSubmenu, onMouseEnter],
  );

  const handleLinkMouseEnter = useCallback<React.MouseEventHandler<HTMLAnchorElement>>(
    (event) => {
      openSubmenu();
      onMouseEnter?.(event as unknown as React.MouseEvent<HTMLButtonElement>);
    },
    [openSubmenu, onMouseEnter],
  );

  const activeUsesOutline = active && activeStyle === "outline";
  const hasSelectedSurface = selected || highlighted || (active && !activeUsesOutline);
  const hasEmphasizedText = active || hasSelectedSurface;
  const isDestructive = intent === "destructive";
  const canHover = !disableHoverStyle && !disabled;
  const shouldRenderLink = href != null && !disabled;
  const resolvedRole = role ?? (shouldRenderLink ? undefined : "menuitem");
  // `aria-selected` is required on `option` and meaningless on `menuitem`, so
  // derive it from the row's own selected state rather than asking every listbox
  // caller to remember to pass it alongside `role`.
  const ariaSelected = resolvedRole === "option" ? { "aria-selected": selected } : undefined;
  const handleLinkClick = useCallback<React.MouseEventHandler<HTMLAnchorElement>>(
    (event) => {
      if (isModifiedNavigationClick(event)) return;

      onClick?.(event as unknown as React.MouseEvent<HTMLButtonElement>);
    },
    [onClick],
  );
  const leftSlotContent =
    leftAccessory ?? (leftIcon ? <Icon name={leftIcon} size={leftIconSize[size]} /> : null);
  const rowClassName = classNames(
    ROW_BASE,
    "group disabled:opacity-50",
    heightClass[size].default,
    isDestructive ? DESTRUCTIVE_LABEL_TEXT : DEFAULT_LABEL_TEXT,
    // CSS `:hover` still matches disabled buttons, so gate hover classes in JS.
    canHover && (isDestructive ? DESTRUCTIVE_HOVER_TEXT : DEFAULT_LABEL_HOVER_TEXT),
    canHover && (isDestructive ? DESTRUCTIVE_LEFT_ICON_HOVER_TEXT : DEFAULT_LEFT_ICON_HOVER_TEXT),
    // Only `selected` suppresses the pointer. `active` is a composite widget's
    // keyboard cursor, so it remains clickable and pointer hover stays an
    // independent CSS surface.
    selected ? "cursor-default disabled:cursor-not-allowed" : INTERACTIVE_CURSOR,
    hasSelectedSurface
      ? ROW_SELECTED_BG
      : canHover && (isDestructive ? DESTRUCTIVE_HOVER_BG : ROW_HOVER_BG),
    activeUsesOutline && ROW_ACTIVE_OUTLINE,
    hasEmphasizedText &&
      (isDestructive ? ACTIVE_DESTRUCTIVE_TEXT : `${ACTIVE_LABEL_TEXT} ${ACTIVE_LEFT_ICON_TEXT}`),
    className,
  );
  const rowContent = (
    <>
      {leftSlotContent != null || reserveLeftSlot ? (
        <span
          className={classNames(
            "menu-item-v2-left-icon flex shrink-0 items-center justify-center leading-none transition-colors duration-75",
            leftSlotWidthClass[size],
            alignAccessoriesToLabelLine && "h-cds-16",
            isDestructive ? DESTRUCTIVE_LEFT_ICON_TEXT : DEFAULT_LEFT_ICON_TEXT,
          )}
        >
          {leftSlotContent}
        </span>
      ) : null}
      {labelContent == null ? (
        <span className={classNames("flex-1 min-w-0 truncate font-medium", labelTextClass[size])}>
          {label}
        </span>
      ) : (
        // Caller-owned body. Deliberately not wrapped in the label span: content
        // that wants two lines or its own truncation can't do it inside a
        // `truncate` parent. Row chrome (height, hover, active, selected) still
        // comes from rowClassName, and `label` remains the accessible name.
        <span className={classNames("flex-1 min-w-0", labelTextClass[size])}>{labelContent}</span>
      )}
      {keyboardShortcut && (
        <span
          className={classNames("shrink-0 whitespace-nowrap", shortcutTextClass[size], MUTED_TEXT)}
        >
          {keyboardShortcut}
        </span>
      )}
      {rightAccessory &&
        !hasSubmenu &&
        (alignAccessoriesToLabelLine ? (
          <span className={"flex h-cds-16 shrink-0 items-center leading-none"}>
            {rightAccessory}
          </span>
        ) : (
          rightAccessory
        ))}
      {rightIcon && !hasSubmenu && (
        <Icon
          name={getRenderedRightIcon(rightIcon)}
          size={"sm"}
          className={classNames("shrink-0", MUTED_TEXT, rightIconClassName)}
        />
      )}
      {selected && !hideSelectedIndicator && !hasSubmenu && (
        <Icon name={"checkMark"} size={"sm"} className={classNames("shrink-0", MUTED_TEXT)} />
      )}
      {hasSubmenu && (
        <Icon name={"chevronRight"} size={"sm"} className={classNames("shrink-0", MUTED_TEXT)} />
      )}
    </>
  );
  // labelContent replaces the label span, so `label` is no longer in the DOM and
  // the row would be named by whatever the caller rendered. Name it explicitly
  // instead; an aria-label passed through `rest` still wins.
  const accessibleName =
    labelContent != null && typeof label === "string" ? { "aria-label": label } : undefined;

  const row = shouldRenderLink ? (
    <ChalkLink
      unstyled
      data-menu-item-v2-row={""}
      ref={ref as ForwardedRef<HTMLAnchorElement>}
      href={href}
      shallow={shallow}
      role={resolvedRole}
      openInNewTab={shouldOpenInNewTab}
      showOpenInNewTabIcon={!hideOpenInNewTabIcon}
      openInNewTabIconSize="sm"
      aria-haspopup={hasSubmenu || undefined}
      aria-expanded={hasSubmenu ? submenuOpen : undefined}
      className={rowClassName}
      onClick={handleLinkClick}
      onMouseEnter={handleLinkMouseEnter}
      {...accessibleName}
      {...ariaSelected}
      {...(rest as Omit<
        React.ComponentProps<typeof ChalkLink>,
        "children" | "className" | "href" | "onClick" | "onMouseEnter" | "role"
      >)}
    >
      {rowContent}
    </ChalkLink>
  ) : (
    <button
      data-menu-item-v2-row={""}
      ref={ref as ForwardedRef<HTMLButtonElement>}
      type={type}
      role={resolvedRole}
      aria-haspopup={hasSubmenu || undefined}
      aria-expanded={hasSubmenu ? submenuOpen : undefined}
      disabled={disabled}
      className={rowClassName}
      onClick={onClick}
      onMouseEnter={handleRowMouseEnter}
      {...accessibleName}
      {...ariaSelected}
      {...rest}
    >
      {rowContent}
    </button>
  );

  if (!hasSubmenu) return row;

  return (
    <div
      ref={refs.setReference}
      className={"relative"}
      {...getReferenceProps({ onFocus: openSubmenu })}
    >
      {row}
      {submenuOpen && (
        <div
          // Pops out beside the parent row, top-aligned with the
          // row. Floating UI preserves the 3px gutter and vertical
          // row alignment, then flips/shifts when the submenu
          // would spill beyond the viewport. safePolygon keeps it
          // open while the cursor crosses the gutter diagonally, so
          // no hover-bridge element is needed.
          ref={setFloatingSubmenuRef}
          style={floatingStyles}
          className={"z-30"}
          role={"presentation"}
          data-menu-submenu-layer={true}
          {...getFloatingProps()}
        >
          {submenu}
        </div>
      )}
    </div>
  );
});

// -----------------------------------------------------------------
// Title row — non-interactive section heading.
// -----------------------------------------------------------------

const TitleRow: React.FC<MenuItemV2Props> = ({
  size = "md",
  label,
  showBottomDivider,
  className,
}) => {
  return (
    <div
      role={"presentation"}
      className={classNames(
        "flex items-center w-full cursor-default rounded-cds-3",
        "pl-cds-8 pr-cds-6 py-cds-4",
        heightClass[size].title,
        titleTextClass[size],
        // Section heading: zinc-500 in both themes (the rotation
        // center — same color light/dark). `cds-ink-subtle` is the
        // zinc-500 token. `!` keeps tailwind-merge from collapsing
        // this color class into the preceding `text-cds-*` size class.
        "!text-cds-ink-subtle font-medium",
        "data-[bottom-divider=true]:border-b-[1px]",
        "data-[bottom-divider=true]:border-cds-surface-active",
        "dark:data-[bottom-divider=true]:border-zinc-800",
        "data-[bottom-divider=true]:!rounded-bl-none",
        "data-[bottom-divider=true]:!rounded-br-none",
        className,
      )}
      data-bottom-divider={showBottomDivider ? true : undefined}
    >
      <span className={"truncate"}>{label}</span>
    </div>
  );
};

// -----------------------------------------------------------------
// Search row — magnifier icon + real <input type="search">.
// -----------------------------------------------------------------

const SearchRow: React.FC<MenuItemV2Props> = ({
  size = "md",
  label,
  leftIcon = "search",
  searchValue,
  onSearchChange,
  searchInputType = "search",
  compactSearch = false,
  onSearchKeyDown,
  searchInputRef,
  autoFocus,
  searchAriaActivedescendant,
  searchListboxId,
  showBottomDivider,
  hideSearchIcon = false,
  className,
}) => {
  const inputLabel = typeof label === "string" ? label : "";
  const flushListSearch = showBottomDivider && !compactSearch;

  const searchControls = (
    <div
      role={"presentation"}
      // Carries the submenu-row marker so useAlignedSubmenuLayer aligns a
      // searchable submenu by its search input (its visual first row) with
      // the originating parent row, not by the first option below it.
      data-menu-item-v2-row={""}
      className={classNames(
        "relative flex items-center rounded-cds-3 gap-cds-8",
        flushListSearch ? "w-full pl-cds-10" : "w-full pl-cds-8",
        compactSearch ? "pr-0" : flushListSearch ? "pr-[5px]" : "pr-cds-2",
        compactSearch ? heightClass[size].search : heightClass[size].default,
        "data-[bottom-divider=true]:!rounded-bl-none",
        "data-[bottom-divider=true]:!rounded-br-none",
        className,
      )}
      data-bottom-divider={showBottomDivider ? true : undefined}
    >
      {!hideSearchIcon && (
        <Icon
          name={leftIcon}
          size={leftIconSize[size]}
          className={classNames("shrink-0", MUTED_TEXT, flushListSearch && "ml-px")}
        />
      )}
      <input
        ref={searchInputRef}
        autoFocus={autoFocus}
        data-autofocus={autoFocus || undefined}
        type={searchInputType}
        aria-label={inputLabel}
        placeholder={inputLabel}
        value={searchValue}
        onChange={(e) => onSearchChange?.(e.target.value)}
        onKeyDown={onSearchKeyDown}
        // The listbox is always rendered when a caller names one, so the combobox
        // is by definition expanded; a collapsed one would not be this row.
        // Headless UI menus erase implicit roles, so text controls need explicit semantics.
        role={
          searchListboxId != null
            ? "combobox"
            : searchInputType === "number"
              ? "spinbutton"
              : "searchbox"
        }
        aria-controls={searchListboxId}
        aria-expanded={searchListboxId != null ? true : undefined}
        aria-autocomplete={searchListboxId != null ? "list" : undefined}
        aria-activedescendant={searchAriaActivedescendant}
        className={classNames(
          "!min-h-0 !p-0 !border-0 !bg-transparent !shadow-none",
          "focus:!ring-0 focus:!ring-offset-0 focus:!border-0",
          "focus:!outline-none focus:!shadow-none",
          "[&::-webkit-search-cancel-button]:appearance-none",
          "[&::-webkit-search-cancel-button]:hidden",
          searchInputType === "number" &&
            "[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none",
          "flex-1 min-w-0",
          flushListSearch && hideSearchIcon && "ml-px",
          "!text-zinc-800 dark:!text-zinc-200",
          MUTED_PLACEHOLDER_TEXT,
          labelTextClass[size],
        )}
      />
      {searchValue && onSearchChange && (
        <IconButtonV2
          size={size === "sm" ? "xs" : "sm"}
          variant={"tertiary"}
          icon={"close"}
          aria-label={"Clear search"}
          role="button"
          onClick={() => onSearchChange("")}
          className={classNames("shrink-0", SEARCH_CLEAR_BUTTON_TEXT)}
        />
      )}
    </div>
  );

  if (!flushListSearch) {
    return searchControls;
  }

  return (
    <div role={"presentation"} className={"-mx-cds-3 w-[calc(100%+6px)]"}>
      {searchControls}
      {/* Match the menu section's 3px perimeter inset above and below the
          full-width stroke, so the search header and option list breathe
          symmetrically. */}
      <span aria-hidden={true} className={"block py-cds-3"}>
        <span className={"block h-px w-full bg-cds-surface-active dark:bg-zinc-800"} />
      </span>
    </div>
  );
};

// -----------------------------------------------------------------
// Checkbox row — shares the same visual control as CheckboxV2 while
// keeping the whole row clickable (`menuitemcheckbox`).
// -----------------------------------------------------------------

const CheckboxRow = forwardRef(function CheckboxRowImpl(
  {
    size = "md",
    label,
    labelContent,
    subtitle,
    wrapSubtitle = false,
    rightAccessory,
    checked = false,
    active = false,
    activeStyle = "fill",
    highlighted = false,
    disableHoverStyle = false,
    disabled,
    leftAccessory,
    alignAccessoriesToLabelLine = false,
    className,
    role,
    type = "button",
    ...rest
  }: MenuItemV2Props,
  ref: ForwardedRef<HTMLButtonElement>,
) {
  const hasSubtitle = subtitle != null;
  // Same contract as the default row: caller-owned content replaces the label
  // span, so `label` leaves the DOM and has to name the row explicitly. An
  // aria-label passed through `rest` still wins.
  const accessibleName =
    labelContent != null && typeof label === "string" ? { "aria-label": label } : undefined;
  const activeUsesOutline = active && activeStyle === "outline";
  const hasHighlightedSurface = highlighted || (active && !activeUsesOutline);
  const hasEmphasizedText = active || highlighted;
  const canHover = !disableHoverStyle && !disabled;

  return (
    <button
      data-menu-item-v2-row={""}
      ref={ref}
      type={type}
      // `menuitemcheckbox` is right inside a menu, but a multi-select row inside
      // a listbox has to be an `option` carrying `aria-selected`. Headless UI injects
      // a generic `menuitem` role, which must retain the checkbox semantics here.
      role={role == null || role === "menuitem" ? "menuitemcheckbox" : role}
      {...(role === "option" ? { "aria-selected": checked } : { "aria-checked": checked })}
      disabled={disabled}
      className={classNames(
        ROW_BASE_WITHOUT_LEFT_PADDING,
        checkboxRowLeftPaddingClass[size],
        // A subtitle row grows to fit its two lines (mirroring the two-line
        // variant) and pins the control to the primary label's line box.
        hasSubtitle ? "items-start" : heightClass[size].checkbox,
        DEFAULT_LABEL_TEXT,
        "disabled:opacity-50",
        canHover && DEFAULT_LABEL_HOVER_TEXT,
        INTERACTIVE_CURSOR,
        hasHighlightedSurface ? ROW_SELECTED_BG : canHover && ROW_HOVER_BG,
        activeUsesOutline && ROW_ACTIVE_OUTLINE,
        hasEmphasizedText && ACTIVE_LABEL_TEXT,
        className,
      )}
      {...accessibleName}
      {...rest}
    >
      {/* In a subtitle row (items-start) the 16px control matches the title's
          16px line box exactly, so flush-top alignment centers it on the title
          — unlike two-line icons, which are smaller and need a top offset. */}
      <CheckboxV2Control checked={checked} disabled={disabled} />
      {leftAccessory && (
        <span
          className={classNames(
            "menu-item-v2-left-icon flex shrink-0 items-center justify-center leading-none transition-colors duration-75",
            alignAccessoriesToLabelLine && "h-cds-16",
            DEFAULT_LEFT_ICON_TEXT,
          )}
        >
          {leftAccessory}
        </span>
      )}
      {hasSubtitle ? (
        <span className={"flex min-w-0 flex-1 flex-col gap-cds-2"}>
          {labelContent == null ? (
            <span className={classNames("w-full truncate font-medium", labelTextClass[size])}>
              {label}
            </span>
          ) : (
            <span className={classNames("w-full min-w-0", labelTextClass[size])}>
              {labelContent}
            </span>
          )}
          {/* Same shade as the two-line variant's subtitle. Node subtitles
              get a flex row and own their truncation (e.g. tooltip-on-clip);
              plain strings truncate here. */}
          <span
            className={classNames(
              typeof subtitle !== "string" && "flex",
              wrapSubtitle ? "w-max max-w-full min-w-0" : "w-full min-w-0 truncate",
              titleTextClass[size],
              "!text-cds-ink-muted dark:!text-zinc-500",
            )}
          >
            {subtitle}
          </span>
        </span>
      ) : labelContent == null ? (
        <span className={classNames("flex-1 min-w-0 truncate font-medium", labelTextClass[size])}>
          {label}
        </span>
      ) : (
        // Not wrapped in the truncating label span, for the same reason the
        // default row does not: content that wants two lines or its own
        // truncation cannot do it inside a `truncate` parent.
        <span className={classNames("flex-1 min-w-0", labelTextClass[size])}>{labelContent}</span>
      )}
      {rightAccessory &&
        (alignAccessoriesToLabelLine ? (
          <span className={"flex h-cds-16 shrink-0 items-center leading-none"}>
            {rightAccessory}
          </span>
        ) : (
          rightAccessory
        ))}
    </button>
  );
});

// -----------------------------------------------------------------
// Two-line row — optional icon + label/subtitle stacked.
// -----------------------------------------------------------------

const TwoLineRow = forwardRef(function TwoLineRowImpl(
  {
    size = "md",
    label,
    labelContent,
    subtitle,
    wrapSubtitle = false,
    leftIcon,
    leftAccessory,
    reserveLeftSlot = false,
    rightAccessory,
    alignAccessoriesToLabelLine = false,
    active = false,
    activeStyle = "fill",
    selected = false,
    hideSelectedIndicator = false,
    highlighted = false,
    submenu,
    disableHoverStyle = false,
    disabled,
    href,
    shouldOpenInNewTab,
    hideOpenInNewTabIcon = false,
    className,
    role,
    type = "button",
    onClick,
    onMouseEnter,
    ...rest
  }: MenuItemV2Props,
  ref: ForwardedRef<MenuItemV2InteractiveElement>,
) {
  const hasSubmenu = submenu != null;
  const [submenuOpen, setSubmenuOpen] = useState(false);
  const { submenuTopOffset: submenuTop, updateSubmenuTop } = useAlignedSubmenuLayer(submenuOpen);
  // Floating UI supplies only the hover context here — safePolygon
  // needs the reference/floating elements and the submenu's side. The
  // submenu keeps its CSS `left-full` positioning, so the computed
  // floating styles are intentionally unused.
  const { refs, context } = useFloating({
    open: submenuOpen,
    onOpenChange: setSubmenuOpen,
    placement: "right-start",
    strategy: "absolute",
  });
  const { getReferenceProps, getFloatingProps } = useSubmenuHoverInteractions(context, hasSubmenu);

  const setSubmenuRef = useCallback(
    (node: HTMLDivElement | null) => {
      refs.setFloating(node);
      updateSubmenuTop(node);
      if (node == null) return;
      // No Floating UI positioning here — derive available height from the trigger's viewport position.
      const triggerTop = node.offsetParent?.getBoundingClientRect().top ?? 0;
      const available = Math.max(0, window.innerHeight - triggerTop - 8);
      node.style.setProperty("--submenu-available-height", `${available}px`);
    },
    [refs, updateSubmenuTop],
  );

  // useHover covers real pointer traffic on the wrapper; this direct
  // path additionally opens on synthetic mouseenter dispatched straight
  // at the row (keyboard flows and tests).
  const openSubmenu = useCallback(() => {
    if (!hasSubmenu) return;
    setSubmenuOpen(true);
  }, [hasSubmenu]);

  const handleRowMouseEnter = useCallback<React.MouseEventHandler<HTMLButtonElement>>(
    (event) => {
      openSubmenu();
      onMouseEnter?.(event);
    },
    [openSubmenu, onMouseEnter],
  );

  const handleLinkMouseEnter = useCallback<React.MouseEventHandler<HTMLAnchorElement>>(
    (event) => {
      openSubmenu();
      onMouseEnter?.(event as unknown as React.MouseEvent<HTMLButtonElement>);
    },
    [openSubmenu, onMouseEnter],
  );

  const shouldRenderLink = href != null && !disabled;
  const resolvedRole = role ?? (shouldRenderLink ? undefined : "menuitem");
  // Mirrors the default row: `aria-selected` is required on `option` and
  // meaningless on `menuitem`, so it is derived here rather than asked of every
  // listbox caller. `rest` still wins, for the palette rows that map the
  // attribute onto their own cursor rather than a committed value.
  const ariaSelected = resolvedRole === "option" ? { "aria-selected": selected } : undefined;
  // Also the same contract as the default row: caller-owned content replaces the
  // label span, so `label` leaves the DOM and has to name the row explicitly. An
  // aria-label passed through `rest` still wins.
  const accessibleName =
    labelContent != null && typeof label === "string" ? { "aria-label": label } : undefined;
  const handleLinkClick = useCallback<React.MouseEventHandler<HTMLAnchorElement>>(
    (event) => {
      if (isModifiedNavigationClick(event)) return;

      onClick?.(event as unknown as React.MouseEvent<HTMLButtonElement>);
    },
    [onClick],
  );
  const activeUsesOutline = active && activeStyle === "outline";
  const hasSelectedSurface = selected || highlighted || (active && !activeUsesOutline);
  const hasEmphasizedText = active || hasSelectedSurface;
  const canHover = !disableHoverStyle && !disabled;
  const rowClassName = classNames(
    "flex items-start w-full rounded-cds-3",
    "pl-cds-8 pr-cds-6 py-cds-6 gap-cds-8",
    // Background excluded from the transition for the reason ROW_BASE documents.
    "transition-[color,border-color,text-decoration-color,fill,stroke] duration-75",
    "text-left disabled:opacity-50",
    DEFAULT_LABEL_TEXT,
    canHover && DEFAULT_LABEL_HOVER_TEXT,
    canHover && DEFAULT_LEFT_ICON_HOVER_TEXT,
    selected ? "cursor-default disabled:cursor-not-allowed" : INTERACTIVE_CURSOR,
    hasSelectedSurface ? ROW_SELECTED_BG : canHover && ROW_HOVER_BG,
    activeUsesOutline && ROW_ACTIVE_OUTLINE,
    hasEmphasizedText && `${ACTIVE_LABEL_TEXT} ${ACTIVE_LEFT_ICON_TEXT}`,
    className,
  );
  const leftSlotContent =
    leftAccessory ?? (leftIcon ? <Icon name={leftIcon} size={leftIconSize[size]} /> : null);
  const leftContent =
    leftSlotContent == null && !reserveLeftSlot ? null : (
      <span
        className={classNames(
          "menu-item-v2-left-icon flex shrink-0 items-center justify-center transition-colors duration-75",
          leftSlotWidthClass[size],
          alignAccessoriesToLabelLine ? "h-cds-16 leading-none" : twoLineIconOffsetClass[size],
          DEFAULT_LEFT_ICON_TEXT,
        )}
      >
        {leftSlotContent}
      </span>
    );
  const labelLine =
    labelContent == null ? (
      <span
        className={classNames(
          alignAccessoriesToLabelLine ? "min-w-0 flex-1" : "w-full",
          "truncate font-medium",
          labelTextClass[size],
        )}
      >
        {label}
      </span>
    ) : (
      // Same contract as the default row: caller-owned content is not put
      // inside the truncating label span, so it can lay itself out.
      <span
        className={classNames(
          "min-w-0",
          alignAccessoriesToLabelLine ? "flex-1" : "w-full",
          labelTextClass[size],
        )}
      >
        {labelContent}
      </span>
    );
  const subtitleLine = subtitle && (
    // Subtitle is one shade more prominent than other muted
    // text (keyboard shortcuts, title variant, chevrons) —
    // zinc-400 light / zinc-500 dark, matching FieldLabelV2's
    // description. Still clearly secondary to the label, but
    // readable enough to carry meaningful copy.
    <span
      className={classNames(
        wrapSubtitle ? "w-max max-w-full min-w-0" : "w-full truncate",
        // In the label-line layout's grid, skip the leading slot's column to start under the label.
        alignAccessoriesToLabelLine && leftContent != null && "col-start-2",
        titleTextClass[size],
        "!text-cds-ink-muted dark:!text-zinc-500",
      )}
    >
      {subtitle}
    </span>
  );
  const submenuIcon = hasSubmenu ? (
    <Icon
      name={"chevronRight"}
      size={"sm"}
      className={classNames("shrink-0", !alignAccessoriesToLabelLine && "mt-cds-2", MUTED_TEXT)}
    />
  ) : null;
  const rightContent =
    rightAccessory && !hasSubmenu ? (
      <span
        className={classNames(
          "shrink-0",
          alignAccessoriesToLabelLine ? "flex h-cds-16 items-center leading-none" : "mt-cds-1",
        )}
      >
        {rightAccessory}
      </span>
    ) : null;
  const selectedIcon =
    selected && !hideSelectedIndicator && !hasSubmenu ? (
      <Icon
        name={"checkMark"}
        size={"sm"}
        className={classNames("shrink-0", !alignAccessoriesToLabelLine && "mt-cds-2", MUTED_TEXT)}
      />
    ) : null;
  const rowContent = alignAccessoriesToLabelLine ? (
    // The leading slot is its own column, so the subtitle starts on the label's edge however wide
    // the slot's content is, while the trailing accessories stay on the label's line.
    <span
      className={classNames(
        "grid w-full min-w-0 flex-1 items-center gap-x-cds-8 gap-y-cds-2",
        leftContent == null ? "grid-cols-[minmax(0,1fr)]" : "grid-cols-[auto_minmax(0,1fr)]",
      )}
    >
      {leftContent}
      <span className={"flex min-w-0 items-center gap-cds-8"}>
        {labelLine}
        {rightContent}
        {selectedIcon}
        {submenuIcon}
      </span>
      {subtitleLine}
    </span>
  ) : (
    <>
      {leftContent}
      <span className={"flex min-w-0 flex-1 flex-col gap-cds-2"}>
        {labelLine}
        {subtitleLine}
      </span>
      {submenuIcon}
      {rightContent}
      {selectedIcon}
    </>
  );

  const row = shouldRenderLink ? (
    <ChalkLink
      unstyled
      data-menu-item-v2-row={""}
      ref={ref as ForwardedRef<HTMLAnchorElement>}
      href={href}
      role={resolvedRole}
      openInNewTab={shouldOpenInNewTab}
      showOpenInNewTabIcon={!hideOpenInNewTabIcon}
      openInNewTabIconSize="sm"
      aria-haspopup={hasSubmenu || undefined}
      aria-expanded={hasSubmenu ? submenuOpen : undefined}
      className={rowClassName}
      onClick={handleLinkClick}
      onMouseEnter={handleLinkMouseEnter}
      {...ariaSelected}
      {...accessibleName}
      {...(rest as Omit<
        React.ComponentProps<typeof ChalkLink>,
        "children" | "className" | "href" | "onClick" | "onMouseEnter" | "role"
      >)}
    >
      {rowContent}
    </ChalkLink>
  ) : (
    <button
      data-menu-item-v2-row={""}
      ref={ref as ForwardedRef<HTMLButtonElement>}
      type={type}
      role={resolvedRole}
      aria-haspopup={hasSubmenu || undefined}
      aria-expanded={hasSubmenu ? submenuOpen : undefined}
      disabled={disabled}
      className={rowClassName}
      onClick={onClick}
      onMouseEnter={handleRowMouseEnter}
      {...ariaSelected}
      {...accessibleName}
      {...rest}
    >
      {rowContent}
    </button>
  );

  if (!hasSubmenu) return row;

  return (
    <div
      ref={refs.setReference}
      className={"relative"}
      {...getReferenceProps({ onFocus: openSubmenu })}
    >
      {row}
      {submenuOpen && (
        <div
          className={classNames(
            "absolute left-full ml-cds-3 z-30",
            submenuTop == null && "-top-cds-3",
          )}
          ref={setSubmenuRef}
          style={submenuTop == null ? undefined : { top: submenuTop }}
          role={"presentation"}
          data-menu-submenu-layer={true}
          {...getFloatingProps()}
        >
          {submenu}
        </div>
      )}
    </div>
  );
});
